import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { FileText, Upload, Loader2, Check, Calendar, Building2, User, MapPin, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FDCForm = () => {
  const [loading, setLoading] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [dshClfFile, setDshClfFile] = useState(null);
  const [otherFiles, setOtherFiles] = useState([]);
  const fileInputRef = useRef(null);
  const dshClfInputRef = useRef(null);
  const otherFilesInputRef = useRef(null);
  const [formData, setFormData] = useState({
    // Company info
    company_name: '',
    company_legal_form: '',
    company_address: '',
    company_siren: '',
    company_rsac_number: '',
    company_rsac_city: '',
    // Person info
    person_name: '',
    person_function: '',
    // Commercial info
    commercial_firstname: '',
    commercial_lastname: '',
    commercial_email: '',
    commercial_phone: '',
    contract_date: '',
    contract_type: 'Agent commercial',
  });

  const makeFakeFile = (name) => {
    // Minimal valid PDF (~200 bytes) — 1-page blank PDF
    const pdfContent = '%PDF-1.4\n1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj\n2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj\n3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 612 792]>>endobj\nxref\n0 4\n0000000000 65535 f\n0000000009 00000 n\n0000000053 00000 n\n0000000101 00000 n\ntrailer<</Size 4/Root 1 0 R>>\nstartxref\n149\n%%EOF';
    const blob = new Blob([pdfContent], { type: 'application/pdf' });
    return new File([blob], name, { type: 'application/pdf' });
  };

  const autoFillTestData = () => {
    const today = new Date().toISOString().split('T')[0];
    const rand = Math.floor(1000 + Math.random() * 9000);
    setFormData({
      company_name: `Test SARL Lorem ${rand}`,
      company_legal_form: 'SARL',
      company_address: '12 rue de la Republique, 75001 Paris',
      company_siren: '123456789',
      company_rsac_number: 'RSAC-2026-' + rand,
      company_rsac_city: 'Paris',
      person_name: 'Jean Dupont Test',
      person_function: 'Gerant',
      commercial_firstname: 'Lorem',
      commercial_lastname: `Ipsum${rand}`,
      commercial_email: `test.ac.${rand}@lorem-test.fr`,
      commercial_phone: '0612345678',
      contract_date: today,
      contract_type: 'Agent commercial',
    });
    setContractFile(makeFakeFile(`contrat-test-${rand}.pdf`));
    setDshClfFile(makeFakeFile(`dsh-clf-test-${rand}.pdf`));
    setOtherFiles([makeFakeFile(`avenant-test-${rand}.pdf`)]);
    toast.success('Formulaire pre-rempli avec donnees de test');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.company_name || !formData.company_legal_form || !formData.company_address) {
      toast.error('Veuillez remplir tous les champs obligatoires de la societe');
      return;
    }
    
    if (!formData.person_name) {
      toast.error('Veuillez remplir le nom et prenom');
      return;
    }
    
    if (!formData.commercial_firstname || !formData.commercial_lastname || !formData.commercial_email) {
      toast.error('Veuillez remplir tous les champs obligatoires du commercial');
      return;
    }
    
    if (!formData.contract_date) {
      toast.error('Veuillez renseigner la date de signature du contrat');
      return;
    }
    
    if (!contractFile) {
      toast.error('Veuillez joindre le contrat');
      return;
    }
    
    setLoading(true);
    
    try {
      // Step 1: Create FDC
      const response = await fetch(`${API}/rh/fdc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la creation');
      }
      
      const { fdc_id } = await response.json();
      
      // Step 2: Upload contract file
      const fileFormData = new FormData();
      fileFormData.append('file', contractFile);
      
      const uploadResponse = await fetch(`${API}/rh/fdc/${fdc_id}/upload-contract`, {
        method: 'POST',
        credentials: 'include',
        body: fileFormData,
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Erreur lors de l\'upload du contrat');
      }
      
      // Step 3: Upload DSH-CLF file if provided
      if (dshClfFile) {
        const dshFormData = new FormData();
        dshFormData.append('file', dshClfFile);
        dshFormData.append('doc_type', 'dsh_clf');
        
        const dshResp = await fetch(`${API}/rh/fdc/${fdc_id}/upload-document`, {
          method: 'POST',
          credentials: 'include',
          body: dshFormData,
        });
        if (!dshResp.ok) {
          toast.error('Echec de l\'upload du fichier DSH-CLF');
        }
      }
      
      // Step 4: Upload other files if provided
      for (const file of otherFiles) {
        const otherFormData = new FormData();
        otherFormData.append('file', file);
        otherFormData.append('doc_type', 'autres_avenants');
        
        const otherResp = await fetch(`${API}/rh/fdc/${fdc_id}/upload-document`, {
          method: 'POST',
          credentials: 'include',
          body: otherFormData,
        });
        if (!otherResp.ok) {
          toast.error(`Echec de l'upload: ${file.name}`);
        }
      }
      
      toast.success('Declaration creee avec succes');
      
      // Reset form
      setFormData({
        company_name: '',
        company_legal_form: '',
        company_address: '',
        company_siren: '',
        company_rsac_number: '',
        company_rsac_city: '',
        person_name: '',
        person_function: '',
        commercial_firstname: '',
        commercial_lastname: '',
        commercial_email: '',
        commercial_phone: '',
        contract_date: '',
        contract_type: 'Agent commercial',
      });
      setContractFile(null);
      setDshClfFile(null);
      setOtherFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (dshClfInputRef.current) dshClfInputRef.current.value = '';
      if (otherFilesInputRef.current) otherFilesInputRef.current.value = '';
      
    } catch (error) {
      console.error('FDC creation error:', error);
      toast.error(error.message || 'Erreur lors de la creation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="fdc-form-title">
          Declaration de Contrat
        </h1>
        <p className="text-slate-500 mt-1">
          Formulaire de Declaration de Contrat (FDC) pour un nouvel agent commercial
        </p>
      </div>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-slate-900">Comment ca fonctionne ?</p>
              <p className="text-sm text-slate-600 mt-1">
                1. Remplissez les informations de la societe et du commercial recrute<br/>
                2. Joignez le contrat signe<br/>
                3. La declaration sera soumise a validation par l'administration<br/>
                4. Une fois validee, le compte commercial sera cree automatiquement
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Informations de la societe
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Nom de la societe *</Label>
                <Input
                  id="company_name"
                  data-testid="company-name-input"
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  placeholder="Ex: ACME SARL"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_legal_form">Forme sociale *</Label>
                <Select
                  value={formData.company_legal_form}
                  onValueChange={(value) => setFormData({...formData, company_legal_form: value})}
                >
                  <SelectTrigger data-testid="company-legal-form-select">
                    <SelectValue placeholder="Selectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SARL">SARL</SelectItem>
                    <SelectItem value="SAS">SAS</SelectItem>
                    <SelectItem value="SASU">SASU</SelectItem>
                    <SelectItem value="EURL">EURL</SelectItem>
                    <SelectItem value="SA">SA</SelectItem>
                    <SelectItem value="SNC">SNC</SelectItem>
                    <SelectItem value="Auto-entrepreneur">Auto-entrepreneur</SelectItem>
                    <SelectItem value="Entreprise individuelle">Entreprise individuelle</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_address">Adresse complete *</Label>
              <Input
                id="company_address"
                data-testid="company-address-input"
                value={formData.company_address}
                onChange={(e) => setFormData({...formData, company_address: e.target.value})}
                placeholder="Ex: 123 Rue de la Paix, 75001 Paris"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_siren">SIREN</Label>
                <Input
                  id="company_siren"
                  data-testid="company-siren-input"
                  value={formData.company_siren}
                  onChange={(e) => setFormData({...formData, company_siren: e.target.value})}
                  placeholder="Ex: 123456789"
                  maxLength={9}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_rsac_number">Numero RSAC</Label>
                <Input
                  id="company_rsac_number"
                  data-testid="company-rsac-number-input"
                  value={formData.company_rsac_number}
                  onChange={(e) => setFormData({...formData, company_rsac_number: e.target.value})}
                  placeholder="Ex: 123456789"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_rsac_city">Ville RSAC</Label>
                <Input
                  id="company_rsac_city"
                  data-testid="company-rsac-city-input"
                  value={formData.company_rsac_city}
                  onChange={(e) => setFormData({...formData, company_rsac_city: e.target.value})}
                  placeholder="Ex: Paris"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Person Information */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Representant legal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="person_name">Nom Prenom *</Label>
                <Input
                  id="person_name"
                  data-testid="person-name-input"
                  value={formData.person_name}
                  onChange={(e) => setFormData({...formData, person_name: e.target.value})}
                  placeholder="Ex: Jean Dupont"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="person_function">Fonction</Label>
                <Input
                  id="person_function"
                  data-testid="person-function-input"
                  value={formData.person_function}
                  onChange={(e) => setFormData({...formData, person_function: e.target.value})}
                  placeholder="Ex: Gerant"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Commercial Information */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-indigo-600" />
              Informations du commercial
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstname">Prenom *</Label>
                <Input
                  id="firstname"
                  data-testid="commercial-firstname-input"
                  value={formData.commercial_firstname}
                  onChange={(e) => setFormData({...formData, commercial_firstname: e.target.value})}
                  placeholder="Jean"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastname">Nom *</Label>
                <Input
                  id="lastname"
                  data-testid="commercial-lastname-input"
                  value={formData.commercial_lastname}
                  onChange={(e) => setFormData({...formData, commercial_lastname: e.target.value})}
                  placeholder="Dupont"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="commercial-email-input"
                  value={formData.commercial_email}
                  onChange={(e) => setFormData({...formData, commercial_email: e.target.value})}
                  placeholder="jean.dupont@email.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Telephone</Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="commercial-phone-input"
                  value={formData.commercial_phone}
                  onChange={(e) => setFormData({...formData, commercial_phone: e.target.value})}
                  placeholder="06 12 34 56 78"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contract_date">Date de signature du contrat <span className="text-red-500">*</span></Label>
                <p className="text-xs text-slate-500">Cette date sera utilisee comme date de debut DEC de l'AC</p>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="contract_date"
                    type="date"
                    required
                    className="pl-10"
                    data-testid="contract-date-input"
                    value={formData.contract_date}
                    onChange={(e) => setFormData({...formData, contract_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contract_type">Type de contrat</Label>
                <Select
                  value={formData.contract_type}
                  onValueChange={(value) => setFormData({...formData, contract_type: value})}
                >
                  <SelectTrigger data-testid="contract-type-select">
                    <SelectValue placeholder="Selectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Agent commercial">Agent commercial</SelectItem>
                    <SelectItem value="Mandataire">Mandataire</SelectItem>
                    <SelectItem value="Apporteur d'affaires">Apporteur d'affaires</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Contrat signe (PDF) *</Label>
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  data-testid="contract-file-input"
                  onChange={(e) => setContractFile(e.target.files[0])}
                />
                {contractFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <Check className="w-5 h-5 text-emerald-600" />
                    <span className="text-emerald-600 font-medium">{contractFile.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setContractFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      Changer
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choisir un fichier
                  </Button>
                )}
              </div>
            </div>

            {/* DSH-CLF signé */}
            <div className="space-y-2">
              <Label>DSH – CLF signe (PDF)</Label>
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                <input
                  type="file"
                  ref={dshClfInputRef}
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  data-testid="dsh-clf-file-input"
                  onChange={(e) => setDshClfFile(e.target.files[0])}
                />
                {dshClfFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <Check className="w-5 h-5 text-emerald-600" />
                    <span className="text-emerald-600 font-medium">{dshClfFile.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDshClfFile(null);
                        if (dshClfInputRef.current) dshClfInputRef.current.value = '';
                      }}
                    >
                      Changer
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => dshClfInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choisir un fichier
                  </Button>
                )}
              </div>
            </div>

            {/* Autres & Avenants */}
            <div className="space-y-2">
              <Label>Autres & Avenants (jusqu'a 2 fichiers PDF)</Label>
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                <input
                  type="file"
                  ref={otherFilesInputRef}
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  multiple
                  data-testid="other-files-input"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []).slice(0, 2);
                    setOtherFiles(files);
                  }}
                />
                {otherFiles.length > 0 ? (
                  <div className="space-y-2">
                    {otherFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-center gap-2">
                        <Check className="w-5 h-5 text-emerald-600" />
                        <span className="text-emerald-600 font-medium">{file.name}</span>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setOtherFiles([]);
                        if (otherFilesInputRef.current) otherFilesInputRef.current.value = '';
                      }}
                    >
                      Supprimer
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => otherFilesInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choisir des fichiers
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Button
          type="submit"
          className="w-full h-12 bg-indigo-600 hover:bg-indigo-700"
          disabled={loading}
          data-testid="submit-fdc-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Envoi en cours...
            </>
          ) : (
            <>
              <FileText className="w-4 h-4 mr-2" />
              Soumettre la declaration
            </>
          )}
        </Button>
      </form>
    </div>
  );
};
